/**
 * Facility inspections — Asset Management.
 *
 * Every facility, inspected by each trade that looks after it — civil,
 * electrical, electro-mechanical, fire systems, utilities, cleaning and pest
 * control — each through its own contractor, on a short daily round and a
 * detailed inspection. The condition each trade reads, the findings still
 * open, the work orders raised from them (in the shared register, so
 * Operations control works the same orders), the repair history compared year
 * on year and by type, and next year's forecast.
 *
 * "Viewing" narrows everything to one trade: what a contractor's supervisor
 * would want in front of them, and what a report for that contractor covers.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, Field, Input, Segmented, Select, Skeleton, StatusBadge } from "../../design/components";
import {
  BAND_LABEL,
  BANDS,
  CONDITION_FACTOR,
  BACKLOG_WEIGHT,
  FORM_LABEL,
  RESULT_LABEL,
  TRADES,
  averageCost,
  byRepairType,
  byYear,
  contractorFor,
  defectsOf,
  forecast,
  inspectionsFor,
  ordersFor,
  repairs,
  scoreOf,
  standing,
  tradeOfOrder,
  withoutSample,
  type InspectionRecord,
  type InspectionStore,
  type OpenFinding,
  type Standing,
  type TradeId,
} from "../../domain/assetInspections";
import { FACILITY_BY_ID, FACILITY_LIST } from "../../domain/facilityRegister";
import { current } from "../../domain/contracts";
import { STATUS_LABEL, isOpen, legalTransitions, type WorkOrder, type WorkOrderStatus } from "../../domain/workOrders";
import { formatDate } from "../../lib/format";
import { Page } from "../../layout/AppShell";
import { t, tn } from "../../i18n";
import { useContracts } from "../contracts/ContractsRegister";
import { useAllWorkOrders, useInspectionStore, useWorkOrderActions } from "./facilityData";
import { InspectionForm, type FormStart } from "./InspectionForm";
import { InspectionReports } from "./InspectionReports";
import { RegisterNotices } from "../shared/RegisterNotices";
import { itemText, sar } from "./labels";
import s from "./FacilityInspections.module.css";

type Tab = "facilities" | "findings" | "history" | "forecast" | "reports" | "contractors";

const TABS: { value: Tab; label: string }[] = [
  { value: "facilities", label: "Facilities" },
  { value: "findings", label: "Findings and work orders" },
  { value: "history", label: "History and comparison" },
  { value: "forecast", label: "Forecast" },
  { value: "reports", label: "Reports" },
  { value: "contractors", label: "Contractors" },
];

export function FacilityInspectionsPage() {
  const session = useSession();
  const entered = useInspectionStore();
  const store = entered.value;
  const { orders } = useAllWorkOrders();
  const [tab, setTab] = useState<Tab>("facilities");
  const [viewing, setViewing] = useState<TradeId | "all">("all");
  const [selected, setSelected] = useState<string | null>(null);
  const [formStart, setFormStart] = useState<FormStart | null>(null);
  const trades = viewing === "all" ? TRADES : TRADES.filter((x) => x.id === viewing);
  const now = useMemo(() => new Date(), []);

  /* only what changed is sent, from the versions on screen; a refusal is thrown for the editor to show */
  const save = (next: InspectionStore, base?: InspectionStore) => entered.commit(next, base);
  const sampleCount = store.inspections.filter((r) => r.sample).length + store.history.filter((h) => h.sample).length;

  /* the whole airport at a glance, for the tiles and for Sky */
  const stats = useMemo(() => {
    let open = { critical: 0, major: 0, minor: 0 };
    let unraised = 0;
    const bands: Record<string, number> = {};
    for (const f of FACILITY_LIST)
      for (const x of trades) {
        const st = standing(store, f.id, x.id);
        if (st.band) bands[st.band] = (bands[st.band] ?? 0) + 1;
        for (const o of st.open) open = { ...open, [o.fault.result]: open[o.fault.result as keyof typeof open] + 1 };
        unraised += st.unraised;
      }
    const recent = store.inspections.filter((r) => (viewing === "all" || r.trade === viewing) && Date.parse(r.at) > now.getTime() - 30 * 86_400_000).length;
    const fromInspections = ordersFor(orders, undefined, viewing === "all" ? undefined : viewing);
    const budget = forecast(store, orders, now, viewing === "all" ? undefined : viewing).reduce((n, r) => n + r.budget, 0);
    return { open, unraised, bands, recent, openOrders: fromInspections.filter((o) => isOpen(o.status)).length, budget };
  }, [store, orders, trades, viewing, now]);

  useScreenContext({
    route: "/facility-inspections",
    title: "Facility inspections",
    summary: [
      `${stats.open.critical} critical, ${stats.open.major} major and ${stats.open.minor} minor findings open across ${FACILITY_LIST.length} facilities; ${stats.unraised} have no work order yet.`,
      `${stats.recent} inspections in the last 30 days; ${stats.openOrders} work orders from inspections still open.`,
      `Forecast for the next 12 months: ${sar(stats.budget)}${sampleCount ? " (includes sample records)" : ""}.`,
    ],
    figures: new Set(
      [stats.open.critical, stats.open.major, stats.open.minor, stats.unraised, stats.recent, stats.openOrders, FACILITY_LIST.length, 30, 12].map(String),
    ),
    offers: [
      { label: "Which facilities are in the worst condition?", ask: "which facilities are in the worst condition" },
      { label: "What is forecast to cost most next year?", ask: "what is forecast to cost most next year" },
    ],
    own: (q) => {
      const lower = q.toLowerCase();
      if (/worst|poor|condition/.test(lower)) {
        const all = FACILITY_LIST.flatMap((f) => TRADES.map((x) => standing(store, f.id, x.id))).filter((st) => st.score !== null);
        const worst = all.sort((a, b) => a.score! - b.score!).slice(0, 5);
        return ["The five lowest conditions, from each trade's latest detailed inspection:", ...worst.map((w) => `${w.facility.id} ${w.facility.name} — ${w.trade.label}: ${w.score} (${BAND_LABEL[w.band!]})`)].join("\n");
      }
      if (/forecast|budget|cost most|next year/.test(lower)) {
        const rows = forecast(store, orders, now).slice(0, 5);
        return ["Highest forecast for the next 12 months:", ...rows.map((r) => `${r.facilityId} ${FACILITY_BY_ID.get(r.facilityId)!.name} — ${TRADES.find((x) => x.id === r.trade)!.label}: ${sar(r.budget)}`)].join("\n");
      }
      return null;
    },
  });

  const openForm = (start: Partial<FormStart>) =>
    setFormStart({ facilityId: start.facilityId ?? selected ?? FACILITY_LIST[0].id, trade: start.trade ?? (viewing === "all" ? "civil" : viewing), form: start.form ?? "daily" });

  /* the register is read from the platform: until it is, nothing is drawn as if it were empty */
  if (entered.loading)
    return (
      <Page eyebrow="Asset Management" title="Facility inspections">
        <Skeleton rows={8} />
      </Page>
    );

  return (
    <Page
      eyebrow="Asset Management"
      title="Facility inspections"
      actions={
        <div className={s.bar}>
          <Button onClick={() => openForm({ form: "daily" })}>{t("New daily inspection")}</Button>
          <Button variant="primary" onClick={() => openForm({ form: "detailed" })}>
            {t("New detailed inspection")}
          </Button>
        </div>
      }
    >
      <div className={s.page}>
        <p className={s.lede}>
          {t(
            "Each trade's contractor does a short daily round and a detailed inspection. Defects become work orders in the shared register.",
          )}
        </p>

        {sampleCount > 0 && (
          <div className={s.notice} data-testid="sample-notice">
            <span className={s.sampleTag}>{t("SAMPLE")}</span>
            <span>{t("{n} inspections and repairs here are samples, marked in every report.", { n: sampleCount })}</span>
            <Button
              size="sm"
              onClick={() => {
                if (window.confirm(t("Remove all sample inspections and repairs? Your own records stay."))) void save(withoutSample(store));
              }}
            >
              {t("Remove the sample")}
            </Button>
          </div>
        )}

        <div className={s.bar}>
          <Segmented<Tab> value={tab} options={TABS} onChange={setTab} ariaLabel="Facility inspections views" />
          <span className={s.spacer} />
          <Field label="Viewing">
            <Select value={viewing} onChange={(e) => setViewing(e.target.value as TradeId | "all")} aria-label={t("Viewing")}>
              <option value="all">{t("All trades")}</option>
              {TRADES.map((x) => (
                <option key={x.id} value={x.id}>
                  {t(x.label)} — {contractorFor(store, x.id).company}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <RegisterNotices doc={entered} what={t("facility inspections register")} />

        {formStart && (
          <InspectionForm
            key={`${formStart.facilityId}-${formStart.trade}-${formStart.form}`}
            start={formStart}
            store={store}
            who={session.principal.name}
            onSave={save}
            onClose={() => setFormStart(null)}
          />
        )}

        <div className={s.tiles}>
          <div className={s.tile}>
            <span className={s.tileValue} style={{ color: "var(--red)" }}>
              {stats.open.critical}
            </span>
            <span className={s.tileLabel}>{t("critical findings open")}</span>
          </div>
          <div className={s.tile}>
            <span className={s.tileValue}>{stats.open.major}</span>
            <span className={s.tileLabel}>{t("major findings open")}</span>
          </div>
          <div className={s.tile}>
            <span className={s.tileValue}>{stats.unraised}</span>
            <span className={s.tileLabel}>{t("findings with no work order")}</span>
          </div>
          <div className={s.tile}>
            <span className={s.tileValue}>{stats.openOrders}</span>
            <span className={s.tileLabel}>{t("work orders from inspections open")}</span>
          </div>
          <div className={s.tile}>
            <span className={s.tileValue}>{stats.recent}</span>
            <span className={s.tileLabel}>{t("inspections, last 30 days")}</span>
          </div>
          <div className={s.tile}>
            <span className={s.tileValue}>{sar(stats.budget)}</span>
            <span className={s.tileLabel}>{t("forecast, next 12 months")}</span>
          </div>
        </div>

        {tab === "facilities" && (
          <FacilitiesTab store={store} orders={orders} trades={trades.map((x) => x.id)} selected={selected} onSelect={setSelected} onInspect={openForm} onSave={save} />
        )}
        {tab === "findings" && <FindingsTab store={store} orders={orders} viewing={viewing} onSave={save} />}
        {tab === "history" && <HistoryTab store={store} orders={orders} viewing={viewing} now={now} />}
        {tab === "forecast" && <ForecastTab store={store} orders={orders} viewing={viewing} now={now} />}
        {tab === "reports" && (
          <InspectionReports store={store} orders={orders} site={`${session.site.code} · ${session.site.name}`} who={session.principal.name} initialTrade={viewing} />
        )}
        {tab === "contractors" && <ContractorsTab store={store} onSave={save} />}
      </div>
    </Page>
  );
}

/* ── the condition chip ───────────────────────────────────────────────── */

function BandChip({ st }: { st: Standing }) {
  if (!st.band) return <span className={`${s.band} ${s.none}`}>—</span>;
  return (
    <span className={`${s.band} ${s[st.band]}`} title={`${t(BAND_LABEL[st.band])} · ${t(FORM_LABEL[st.basis!.form])} ${formatDate(st.basis!.at)}`}>
      {st.score}
      {st.change !== null && st.change !== 0 && <span className={st.change > 0 ? s.up : s.down}>{st.change > 0 ? `▲${st.change}` : `▼${-st.change}`}</span>}
    </span>
  );
}

/* ── facilities ───────────────────────────────────────────────────────── */

function FacilitiesTab({
  store,
  orders,
  trades,
  selected,
  onSelect,
  onInspect,
  onSave,
}: {
  store: InspectionStore;
  orders: readonly WorkOrder[];
  trades: TradeId[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  onInspect: (start: Partial<FormStart>) => void;
  onSave: (next: InspectionStore) => Promise<void>;
}) {
  const [group, setGroup] = useState("all");
  const groups = [...new Set(FACILITY_LIST.map((f) => f.group))];
  const list = FACILITY_LIST.filter((f) => group === "all" || f.group === group);
  return (
    <>
      <Card title={t("Condition by facility and trade")} sub={t("From each trade's latest detailed inspection. ▲▼ shows the change.")}>
        <div className={s.bar}>
          <Field label="Group">
            <Select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="all">{t("All groups")}</option>
              {groups.map((g) => (
                <option key={g} value={g}>
                  {t(g)}
                </option>
              ))}
            </Select>
          </Field>
          <span className={s.small}>
            {BANDS.map((b) => (
              <span key={b} className={`${s.band} ${s[b]}`} style={{ marginInlineEnd: 4 }}>
                {t(BAND_LABEL[b])}
              </span>
            ))}
          </span>
        </div>
        <div className={s.scroll}>
          <table className={s.grid} data-testid="facility-grid">
            <thead>
              <tr>
                <th>{t("Facility")}</th>
                <th>{t("Group")}</th>
                {trades.map((x) => (
                  <th key={x}>{t(TRADES.find((y) => y.id === x)!.label)}</th>
                ))}
                <th className={s.num}>{t("Open findings")}</th>
                <th className={s.num}>{t("Open work orders")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((f) => {
                const sts = trades.map((x) => standing(store, f.id, x));
                const open = sts.reduce((n, st) => n + st.open.length, 0);
                const wos = ordersFor(orders, f.id).filter((o) => isOpen(o.status) && (trades.length === TRADES.length || trades.includes(tradeOfOrder(o)!))).length;
                return (
                  <tr
                    key={f.id}
                    className={`${s.pick} ${selected === f.id ? s.selected : ""}`}
                    onClick={() => onSelect(selected === f.id ? null : f.id)}
                    data-testid={`facility-${f.id}`}
                  >
                    <td>
                      <span className={s.mono}>{f.id}</span> {f.name} <span className={s.muted}>C{f.crit}</span>
                    </td>
                    <td className={s.muted}>{t(f.group)}</td>
                    {sts.map((st) => (
                      <td key={st.trade.id}>
                        <BandChip st={st} />
                      </td>
                    ))}
                    <td className={s.num}>{open}</td>
                    <td className={s.num}>{wos}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {selected && <FacilityDetail id={selected} store={store} orders={orders} trades={trades} onInspect={onInspect} onSave={onSave} />}
    </>
  );
}

function FacilityDetail({
  id,
  store,
  orders,
  trades,
  onInspect,
  onSave,
}: {
  id: string;
  store: InspectionStore;
  orders: readonly WorkOrder[];
  trades: TradeId[];
  onInspect: (start: Partial<FormStart>) => void;
  onSave: (next: InspectionStore) => Promise<void>;
}) {
  const f = FACILITY_BY_ID.get(id)!;
  const [record, setRecord] = useState<InspectionRecord | null>(null);
  const history = inspectionsFor(store, id).filter((r) => trades.includes(r.trade)).reverse();
  const wos = ordersFor(orders, id).filter((o) => trades.length === TRADES.length || trades.includes(tradeOfOrder(o)!));
  return (
    <Card title={`${f.id} · ${f.name}`} sub={`${t(f.group)} · ${t("criticality")} C${f.crit}`}>
      <div className={s.page} data-testid="facility-detail">
        <div className={s.two}>
          {trades.map((x) => {
            const st = standing(store, id, x);
            return (
              <div key={x} className={s.tradeCard}>
                <div className={s.tradeHead}>
                  <span className={s.tradeName}>{t(st.trade.label)}</span>
                  <BandChip st={st} />
                  {st.band && <span className={s.small}>{t(BAND_LABEL[st.band])}</span>}
                  <span className={s.spacer} />
                  <Button size="sm" onClick={() => onInspect({ facilityId: id, trade: x, form: "daily" })}>
                    {t("Daily")}
                  </Button>
                  <Button size="sm" onClick={() => onInspect({ facilityId: id, trade: x, form: "detailed" })}>
                    {t("Detailed")}
                  </Button>
                </div>
                <div className={`${s.small} ${s.muted}`}>
                  {st.latest
                    ? t("Last: {form} on {date} by {company}", { form: t(FORM_LABEL[st.latest.form]), date: formatDate(st.latest.at), company: st.latest.company })
                    : t("Never inspected by this trade.")}
                </div>
                <Findings findings={st.open} store={store} onSave={onSave} />
              </div>
            );
          })}
        </div>

        <div>
          <div className={s.sectionTitle}>{t("Inspection history")}</div>
          {history.length === 0 ? (
            <p className={s.muted}>{t("No inspections recorded.")}</p>
          ) : (
            <div className={s.scroll}>
              <table className={s.grid}>
                <thead>
                  <tr>
                    <th>{t("Date")}</th>
                    <th>{t("Trade")}</th>
                    <th>{t("Form")}</th>
                    <th>{t("Company")}</th>
                    <th className={s.num}>{t("Condition")}</th>
                    <th className={s.num}>{t("Defects")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 20).map((r) => (
                    <tr key={r.id} className={s.pick} onClick={() => setRecord(record?.id === r.id ? null : r)}>
                      <td className={s.small}>
                        {formatDate(r.at)} {r.sample && <span className={s.sampleTag}>{t("SAMPLE")}</span>}
                      </td>
                      <td>{t(TRADES.find((x) => x.id === r.trade)!.label)}</td>
                      <td className={s.small}>{t(FORM_LABEL[r.form])}</td>
                      <td className={s.small}>{r.company}</td>
                      <td className={s.num}>{scoreOf(r)}</td>
                      <td className={s.num}>{defectsOf(r).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {record && <RecordView rec={record} />}
        </div>

        <div>
          <div className={s.sectionTitle}>{t("Work orders on this facility")}</div>
          <OrdersTable orders={wos} />
        </div>
      </div>
    </Card>
  );
}

function RecordView({ rec }: { rec: InspectionRecord }) {
  const trade = TRADES.find((x) => x.id === rec.trade)!;
  return (
    <div className={s.tradeCard} data-testid="inspection-record">
      <div className={s.tradeHead}>
        <span className={s.tradeName}>
          {rec.id} · {t(FORM_LABEL[rec.form])}
        </span>
        <span className={s.small}>
          {formatDate(rec.at)} · {rec.company} · {rec.inspector}
        </span>
      </div>
      {rec.faults.length === 0 ? (
        <p className={s.ok}>{t("Every item OK.")}</p>
      ) : (
        <ul className={s.list}>
          {rec.faults.map((f) => (
            <li key={f.itemId} className={s.finding}>
              <span className={`${s.sev} ${s[`sev_${f.result}`]}`}>{t(RESULT_LABEL[f.result])}</span>
              <span>{itemText(trade, f.itemId)}</span>
              {f.note && <span className={s.muted}>— {f.note}</span>}
              {f.workOrderNumber && <span className={s.mono}>{f.workOrderNumber}</span>}
            </li>
          ))}
        </ul>
      )}
      {rec.remarks && <p className={s.small}>{rec.remarks}</p>}
    </div>
  );
}

/* ── open findings, each with a work order to raise ───────────────────── */

const SEV_ORDER = { critical: 0, major: 1, minor: 2 } as const;

function Findings({ findings, store, onSave }: { findings: OpenFinding[]; store: InspectionStore; onSave: (next: InspectionStore) => Promise<void> }) {
  const { raise } = useWorkOrderActions();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (findings.length === 0) return <p className={`${s.small} ${s.ok}`}>{t("Nothing open.")}</p>;
  const sorted = [...findings].sort((a, b) => SEV_ORDER[a.fault.result as keyof typeof SEV_ORDER] - SEV_ORDER[b.fault.result as keyof typeof SEV_ORDER]);
  const go = async (o: OpenFinding) => {
    const key = `${o.inspection.id}|${o.fault.itemId}`;
    setBusy(key);
    setError(null);
    try {
      const { store: next } = await raise(store, o.inspection, o.fault);
      await onSave(next);
    } catch (e) {
      setError(t("A work order was not raised: {why}", { why: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(null);
    }
  };
  const unraised = sorted.filter((o) => !o.fault.workOrderId);
  /* one after another, each saved into the next, so no link is lost between them */
  const all = async () => {
    setBusy("all");
    setError(null);
    try {
      let current = store;
      for (const o of unraised) current = (await raise(current, o.inspection, o.fault)).store;
      await onSave(current);
    } catch (e) {
      setError(t("A work order was not raised: {why}", { why: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      {unraised.length > 1 && (
        <div className={s.bar}>
          <Button size="sm" variant="primary" onClick={() => void all()} disabled={busy !== null}>
            {busy === "all" ? t("Creating…") : t("New Work Orders for all {n}", { n: unraised.length })}
          </Button>
        </div>
      )}
      <ul className={s.list}>
        {sorted.map((o) => {
          const trade = TRADES.find((x) => x.id === o.inspection.trade)!;
          return (
            <li key={`${o.inspection.id}-${o.fault.itemId}`} className={s.finding}>
              <span className={`${s.sev} ${s[`sev_${o.fault.result}`]}`}>{t(RESULT_LABEL[o.fault.result])}</span>
              <span>{itemText(trade, o.fault.itemId)}</span>
              {o.fault.repairType && <span className={s.muted}>· {t(o.fault.repairType)}</span>}
              {o.fault.workOrderNumber ? (
                <span className={s.mono}>{o.fault.workOrderNumber}</span>
              ) : (
                <Button size="sm" onClick={() => void go(o)} disabled={busy !== null}>
                  {busy === `${o.inspection.id}|${o.fault.itemId}` ? t("Creating…") : t("New Work Order")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {error && <p className={s.err}>{error}</p>}
    </>
  );
}

/* ── findings and work orders, across the airport ─────────────────────── */

function FindingsTab({
  store,
  orders,
  viewing,
  onSave,
}: {
  store: InspectionStore;
  orders: readonly WorkOrder[];
  viewing: TradeId | "all";
  onSave: (next: InspectionStore) => Promise<void>;
}) {
  const [severity, setSeverity] = useState<"critical" | "major" | "all">("critical");
  const trades = viewing === "all" ? TRADES : TRADES.filter((x) => x.id === viewing);
  const all = FACILITY_LIST.flatMap((f) =>
    trades.map((x) => ({ st: standing(store, f.id, x.id) })).filter(({ st }) => st.open.some((o) => severity === "all" || o.fault.result === severity || (severity === "major" && o.fault.result === "critical"))),
  );
  const wos = ordersFor(orders, undefined, viewing === "all" ? undefined : viewing);
  return (
    <>
      <Card title={t("Open findings")} sub={t("Worst first. Create a work order from any finding.")}>
        <div className={s.bar}>
          <Segmented
            value={severity}
            onChange={setSeverity}
            ariaLabel="Severity"
            options={[
              { value: "critical", label: "Critical" },
              { value: "major", label: "Major and critical" },
              { value: "all", label: "All" },
            ]}
          />
        </div>
        {all.length === 0 ? (
          <p className={s.ok}>{t("Nothing open at this severity.")}</p>
        ) : (
          <div className={s.two}>
            {all.map(({ st }) => (
              <div key={`${st.facility.id}-${st.trade.id}`} className={s.tradeCard}>
                <div className={s.tradeHead}>
                  <span className={s.tradeName}>
                    {st.facility.id} · {st.facility.name}
                  </span>
                  <span className={s.small}>{t(st.trade.label)}</span>
                  <BandChip st={st} />
                </div>
                <Findings
                  findings={st.open.filter((o) => severity === "all" || o.fault.result === "critical" || (severity === "major" && o.fault.result === "major"))}
                  store={store}
                  onSave={onSave}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card
        title={t("Work orders raised from inspections")}
        sub={t("Same orders as the work-orders board, kept in sync.")}
      >
        <OrdersTable orders={wos} />
      </Card>
    </>
  );
}

const MOVE_LABEL: Partial<Record<WorkOrderStatus, string>> = {
  approved: "Approve",
  dispatched: "Dispatch",
  in_progress: "Start",
  on_hold: "Hold",
  completed: "Complete",
  verified: "Verify",
  cancelled: "Cancel",
};

function OrdersTable({ orders }: { orders: readonly WorkOrder[] }) {
  const { move } = useWorkOrderActions();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [showClosed, setShowClosed] = useState(false);
  const list = orders.filter((o) => showClosed || isOpen(o.status));
  if (orders.length === 0) return <p className={s.muted}>{t("No work orders raised from inspections yet.")}</p>;
  const act = async (o: WorkOrder, to: WorkOrderStatus) => {
    setBusy(o.id);
    setError(null);
    try {
      const cost = Number(costs[o.id]);
      await move(o, {
        to,
        note: t("Changed from facility inspections"),
        ...(to === "on_hold" ? { onHoldReason: t("Held from facility inspections") } : {}),
        ...(to === "completed" && costs[o.id] && Number.isFinite(cost) ? { costActual: cost } : {}),
      });
    } catch (e) {
      /* the platform's own words: a refusal for missing evidence, or segregation of duties */
      setError(`${o.number}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <div className={s.bar}>
        <label className={s.small}>
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> {t("Show closed")}
        </label>
        <span className={s.spacer} />
        <Link to="/work-orders" className={s.small}>
          {t("Open the work-orders board")}
        </Link>
      </div>
      <div className={s.scroll}>
        <table className={s.grid} data-testid="inspection-orders">
          <thead>
            <tr>
              <th>{t("Number")}</th>
              <th>{t("Status")}</th>
              <th>{t("Priority")}</th>
              <th>{t("Title")}</th>
              <th>{t("Opened")}</th>
              <th>{t("Change status")}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => {
              const moves = (o.allowedTransitions ?? legalTransitions(o.status)).filter((x) => MOVE_LABEL[x]);
              return (
                <tr key={o.id}>
                  <td className={s.mono}>
                    <Link to={`/work-orders/${o.id}`}>{o.number}</Link>
                  </td>
                  <td>
                    <StatusBadge status={o.status} />
                  </td>
                  <td className={s.mono}>P{o.priority}</td>
                  <td className={s.small}>{o.title}</td>
                  <td className={s.small}>{formatDate(o.openedAt)}</td>
                  <td>
                    <div className={s.bar}>
                      {moves.includes("completed") && (
                        <Input
                          style={{ width: 110 }}
                          placeholder={t("Cost, SAR")}
                          aria-label={t("Actual cost, SAR, for {number}", { number: o.number })}
                          inputMode="decimal"
                          value={costs[o.id] ?? ""}
                          onChange={(e) => setCosts((c) => ({ ...c, [o.id]: e.target.value }))}
                        />
                      )}
                      {moves.map((to) => (
                        <Button key={to} size="sm" onClick={() => void act(o, to)} disabled={busy !== null} aria-label={`${t(MOVE_LABEL[to]!)} ${o.number}`}>
                          {t(MOVE_LABEL[to]!)}
                        </Button>
                      ))}
                      {moves.length === 0 && <span className={s.muted}>{t(STATUS_LABEL[o.status])}</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <p className={s.err}>{error}</p>}
      <p className={`${s.small} ${s.muted}`}>
        {t("Only allowed status changes are shown. If the platform refuses one, you'll see why. A cost entered at completion feeds the history and forecast.")}
      </p>
    </>
  );
}

/* ── history and comparison ───────────────────────────────────────────── */

function HistoryTab({ store, orders, viewing, now }: { store: InspectionStore; orders: readonly WorkOrder[]; viewing: TradeId | "all"; now: Date }) {
  const all = repairs(store, orders).filter((r) => viewing === "all" || r.trade === viewing);
  const years = byYear(all, now);
  const types = byRepairType(all);
  const trades = viewing === "all" ? TRADES : TRADES.filter((x) => x.id === viewing);
  const maxType = Math.max(1, ...types.map((x) => x.tally.count));
  /* condition compared: each pair's previous detailed inspection against its latest */
  const moves = FACILITY_LIST.flatMap((f) => trades.map((x) => standing(store, f.id, x.id)))
    .filter((st) => st.change !== null)
    .sort((a, b) => a.change! - b.change!);
  const sample = all.some((r) => r.sample);
  return (
    <>
      <Card title={t("Repairs year on year")} sub={t("Repair count and cost per year, by trade{sample}.", { sample: sample ? t(" — includes sample records") : "" })}>
        <div className={s.scroll}>
          <table className={s.grid} data-testid="repairs-by-year">
            <thead>
              <tr>
                <th>{t("Trade")}</th>
                {years.map((y) => (
                  <th key={y.year} className={s.num}>
                    {y.year}
                    {y.year === now.getFullYear() ? ` ${t("(to date)")}` : ""}
                  </th>
                ))}
                <th className={s.num}>{t("Average cost")}</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((x) => {
                const tot = years.reduce((acc, y) => ({ count: acc.count + y.trades[x.id].count, cost: acc.cost + y.trades[x.id].cost, costed: acc.costed + y.trades[x.id].costed }), { count: 0, cost: 0, costed: 0 });
                const avg = averageCost(tot);
                return (
                  <tr key={x.id}>
                    <td>{t(x.label)}</td>
                    {years.map((y) => (
                      <td key={y.year} className={s.num}>
                        {y.trades[x.id].count} · {sar(y.trades[x.id].cost)}
                      </td>
                    ))}
                    <td className={s.num}>{avg === null ? "—" : sar(avg)}</td>
                  </tr>
                );
              })}
              <tr>
                <td>
                  <b>{t("Total")}</b>
                </td>
                {years.map((y) => (
                  <td key={y.year} className={s.num}>
                    <b>
                      {y.total.count} · {sar(y.total.cost)}
                    </b>
                  </td>
                ))}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div className={s.two}>
        <Card title={t("Types of repair")} sub={t("Most frequent first, with what they cost.")}>
          <div className={s.bars} data-testid="repair-types">
            {types.slice(0, 14).map((x) => (
              <div key={`${x.trade}-${x.repairType}`} className={s.barRow}>
                <span>
                  {t(x.repairType)} <span className={s.muted}>· {t(TRADES.find((y) => y.id === x.trade)!.label)}</span>
                </span>
                <span className={s.barTrack}>
                  <span className={s.barFill} style={{ display: "block", width: `${(x.tally.count / maxType) * 100}%` }} />
                </span>
                <span className={s.mono}>
                  {x.tally.count} · {sar(averageCost(x.tally) ?? 0)} {t("avg")}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card title={t("Condition compared")} sub={t("Latest detailed inspection vs. the one before. Biggest drops first.")}>
          <div className={s.scroll}>
            <table className={s.grid} data-testid="condition-compared">
              <thead>
                <tr>
                  <th>{t("Facility")}</th>
                  <th>{t("Trade")}</th>
                  <th className={s.num}>{t("Before")}</th>
                  <th className={s.num}>{t("Now")}</th>
                  <th className={s.num}>{t("Change")}</th>
                </tr>
              </thead>
              <tbody>
                {moves.slice(0, 15).map((st) => (
                  <tr key={`${st.facility.id}-${st.trade.id}`}>
                    <td>
                      <span className={s.mono}>{st.facility.id}</span> {st.facility.name}
                    </td>
                    <td>{t(st.trade.label)}</td>
                    <td className={s.num}>{st.score! - st.change!}</td>
                    <td className={s.num}>
                      <BandChip st={st} />
                    </td>
                    <td className={`${s.num} ${st.change! < 0 ? s.down : s.up}`}>{st.change! > 0 ? `+${st.change}` : st.change}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}

/* ── the forecast ─────────────────────────────────────────────────────── */

function ForecastTab({ store, orders, viewing, now }: { store: InspectionStore; orders: readonly WorkOrder[]; viewing: TradeId | "all"; now: Date }) {
  const rows = forecast(store, orders, now, viewing === "all" ? undefined : viewing);
  const total = rows.reduce((n, r) => n + r.budget, 0);
  const byTrade = TRADES.map((x) => ({ trade: x, budget: rows.filter((r) => r.trade === x.id).reduce((n, r) => n + r.budget, 0), expected: rows.filter((r) => r.trade === x.id).reduce((n, r) => n + r.expected, 0) })).filter((x) => x.budget > 0);
  const max = Math.max(1, ...byTrade.map((x) => x.budget));
  return (
    <>
      <Card title={t("Next 12 months: {total}", { total: sar(total) })} sub={t("Expected repairs plus open findings, at past repair costs.")}>
        <div className={s.bars} data-testid="forecast-by-trade">
          {byTrade.map((x) => (
            <div key={x.trade.id} className={s.barRow}>
              <span>{t(x.trade.label)}</span>
              <span className={s.barTrack}>
                <span className={s.barFill} style={{ display: "block", width: `${(x.budget / max) * 100}%` }} />
              </span>
              <span className={s.mono}>
                {sar(x.budget)} · {tn(Math.round(x.expected), "{n} repair expected", "{n} repairs expected")}
              </span>
            </div>
          ))}
        </div>
        <div className={s.method}>
          <b>{t("How it is worked out")}</b>
          <span>{t("Rate: half the repairs of the last two years, per facility and trade.")}</span>
          <span>
            {t("Condition factor, from the latest detailed inspection: {factors}. Proposed, not calibrated.", {
              factors: BANDS.map((b) => `${t(BAND_LABEL[b])} ×${CONDITION_FACTOR[b]}`).join(", "),
            })}
          </span>
          <span>
            {t("Open findings with no work order count as part of a repair: critical {c}, major {m}, minor {n}.", {
              c: BACKLOG_WEIGHT.critical,
              m: BACKLOG_WEIGHT.major,
              n: BACKLOG_WEIGHT.minor,
            })}
          </span>
          <span>{t("Cost: the facility and trade's average if it has three costed repairs; otherwise the trade's average.")}</span>
        </div>
      </Card>
      <Card title={t("Where the money is forecast to go")} sub={t("Highest first.")}>
        <div className={s.scroll}>
          <table className={s.grid} data-testid="forecast-rows">
            <thead>
              <tr>
                <th>{t("Facility")}</th>
                <th>{t("Trade")}</th>
                <th className={s.num}>{t("Repairs, 2 years")}</th>
                <th>{t("Condition")}</th>
                <th className={s.num}>{t("Expected")}</th>
                <th className={s.num}>{t("Open findings")}</th>
                <th className={s.num}>{t("Average cost")}</th>
                <th className={s.num}>{t("Forecast")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 30).map((r) => {
                const f = FACILITY_BY_ID.get(r.facilityId)!;
                return (
                  <tr key={`${r.facilityId}-${r.trade}`}>
                    <td>
                      <span className={s.mono}>{f.id}</span> {f.name}
                    </td>
                    <td>{t(TRADES.find((x) => x.id === r.trade)!.label)}</td>
                    <td className={s.num}>{r.recent}</td>
                    <td>{r.band ? <span className={`${s.band} ${s[r.band]}`}>{t(BAND_LABEL[r.band])} ×{r.factor}</span> : "—"}</td>
                    <td className={s.num}>{r.expected}</td>
                    <td className={s.num}>{r.backlog}</td>
                    <td className={s.num} title={t(r.costBasis === "own" ? "this facility's own average" : r.costBasis === "trade" ? "the trade's average" : "a proposed typical cost")}>
                      {sar(r.averageCost)}
                    </td>
                    <td className={s.num}>
                      <b>{sar(r.budget)}</b>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

/* ── contractors ──────────────────────────────────────────────────────── */

function ContractorsTab({
  store,
  onSave,
}: {
  store: InspectionStore;
  onSave: (next: InspectionStore, base?: InspectionStore) => Promise<void>;
}) {
  const [opened] = useState(store);
  const contracts = useContracts();
  const list = current(contracts.value).map((v) => v.data);
  const [draft, setDraft] = useState(() => Object.fromEntries(TRADES.map((x) => [x.id, contractorFor(store, x.id)])));
  const [saved, setSaved] = useState(false);
  return (
    <Card title={t("Who inspects what")} sub={t("One contractor per trade, linked to its contract if there is one.")}>
      <div className={s.form} data-testid="contractors">
        {TRADES.map((x) => (
          <div key={x.id} className={s.formHead}>
            <Field label={x.label}>
              <Input
                value={draft[x.id].company}
                onChange={(e) => {
                  setSaved(false);
                  setDraft((d) => ({ ...d, [x.id]: { ...d[x.id], company: e.target.value } }));
                }}
              />
            </Field>
            <Field label="Contract">
              <Select
                value={draft[x.id].contractId ?? ""}
                onChange={(e) => {
                  setSaved(false);
                  const c = list.find((k) => k.id === e.target.value);
                  setDraft((d) => ({ ...d, [x.id]: { ...d[x.id], contractId: e.target.value || undefined, company: c ? c.provider : d[x.id].company } }));
                }}
              >
                <option value="">{t("No contract linked")}</option>
                {list.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.provider} — {c.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Contact">
              <Input
                value={draft[x.id].contact ?? ""}
                onChange={(e) => {
                  setSaved(false);
                  setDraft((d) => ({ ...d, [x.id]: { ...d[x.id], contact: e.target.value || undefined } }));
                }}
              />
            </Field>
          </div>
        ))}
        <div className={s.bar}>
          <Button
            variant="primary"
            onClick={() => {
              /* from the contractors as they stood when this tab opened, so a colleague's change to another trade stands */
              void onSave({ ...opened, contractors: draft }, opened).then(
                () => setSaved(true),
                () => setSaved(false),
              );
            }}
          >
            {t("Save the contractors")}
          </Button>
          {saved && <span className={s.ok}>{t("Saved.")}</span>}
          <Link to="/stakeholders" className={s.small}>
            {t("Open the contracts register")}
          </Link>
        </div>
      </div>
    </Card>
  );
}
